import axios, { Method } from "axios";

/**
 * Calls to the WhatsApp agent's admin API, routed THROUGH the Node backend's
 * /admin/agent/* reverse proxy (see Pureworker-Backend service/admin/agentProxy).
 *
 * Why a proxy and not the agent directly: the agent is a separate origin guarded
 * by a single shared token; proxying keeps that token server-side and lets the
 * dashboard use its normal origin + admin JWT. So every helper here is just a
 * call to `${REACT_APP_BASE_URL}/admin/agent/<agentPath>` with the admin
 * Bearer token, exactly like the other admin calls in utils/apis/func.ts.
 *
 * `<agentPath>` is the agent's own admin path WITHOUT the leading /admin, e.g.
 * agentGet("api/audit-log") -> GET /admin/agent/api/audit-log -> (proxy) ->
 * <AGENT>/admin/api/audit-log.
 */
const baseUrl = process.env.REACT_APP_BASE_URL;

export interface AgentResult<T = any> {
  ok: boolean;
  status: number;
  data: T | null;
  error?: any;
}

async function agentRequest<T = any>(
  method: Method,
  agentPath: string,
  opts: { params?: Record<string, any>; data?: any } = {}
): Promise<AgentResult<T>> {
  const AuthToken = await localStorage.getItem("AuthToken");
  const path = agentPath.replace(/^\/+/, "");
  try {
    const res = await axios.request({
      method,
      timeout: /\/provider-(preview|send)$/.test(path) ? 95000 : 30000,
      url: `${baseUrl}/admin/agent/${path}`,
      params: opts.params,
      data: opts.data,
      headers: { Authorization: `Bearer ${AuthToken}` },
    });
    return { ok: true, status: res.status, data: res.data };
  } catch (error: any) {
    // Match func.ts's non-throwing convention so callers can render an error
    // state instead of crashing. A 502 here means the agent itself is down.
    return {
      ok: false,
      status: error?.response?.status ?? 400,
      data: null,
      error: error?.response?.data ?? error?.message,
    };
  }
}

export const agentGet = <T = any>(p: string, params?: Record<string, any>) =>
  agentRequest<T>("get", p, { params });
export const agentPost = <T = any>(p: string, data?: any) =>
  agentRequest<T>("post", p, { data });
export const agentPatch = <T = any>(p: string, data?: any) =>
  agentRequest<T>("patch", p, { data });
export const agentDelete = <T = any>(p: string) =>
  agentRequest<T>("delete", p);

// --- typed helpers per agent feature (add as pages are ported) --------------

export interface AuditEntry {
  id: number;
  action: string;
  actor: string | null;
  target: string | null;
  detail: any;
  created_at: string;
}

export const getAuditLog = (limit = 200) =>
  agentGet<{ entries: AuditEntry[] }>("api/audit-log", { limit });

// Dispatch jobs board
export const getJobs = (status?: string, limit = 50) =>
  agentGet<{ enabled: boolean; jobs: any[]; metrics: any }>(
    "api/jobs", status ? { status, limit } : { limit });
// Emergency stop for NEW job offers. In-flight jobs are unaffected.
export const getDispatchPause = () => agentGet<any>("api/dispatch-pause");
export const setDispatchPause = (paused: boolean, note?: string, actor?: string) =>
  agentPost<any>("api/dispatch-pause", { paused, note, actor });

// Requests board: connections (intros) and dispatches in one list.
export const getRequests = (sort = "newest", status?: string, limit = 200, groupDuplicates = true) =>
  agentGet<any>("api/requests", { sort, status: status || undefined, limit, group_duplicates: groupDuplicates });
export const getRequestDetail = (kind: string, id: string) =>
  agentGet<any>(`api/requests/${kind}/${encodeURIComponent(id)}`);

export const cancelJob = (jobId: string) => agentPost(`api/jobs/${jobId}/cancel`);
export const forceWave = (jobId: string) => agentPost(`api/jobs/${jobId}/force-wave`);

// Dead-letter queue
export const getDeadLetters = (limit = 100) =>
  agentGet<{ dead_letters: any[] }>("api/dead-letters", { limit });
export const requeueDeadLetter = (rowId: number) =>
  agentPost(`api/dead-letters/${rowId}/requeue`);

// Human-flag review queue
export const getReview = (limit = 100, resolved = false) => agentGet<any[]>("review", { limit, resolved, _refresh: Date.now() });
export const resolveFlag = (flagId: number, resolution_note?: string) =>
  agentPost(`review/${flagId}/resolve`, { resolution_note });
export const getReviewItem = async (flag?: string | null, caseId?: string | null) => {
  if (flag) return agentGet<any>(`review/${encodeURIComponent(flag)}`);
  const result = await agentGet<any>(`cases/${encodeURIComponent(caseId || '')}`);
  if (!result.ok || !result.data) return result;
  const c = result.data;
  if (c.flag_id) return agentGet<any>(`review/${c.flag_id}`);
  return {...result, data: {id: null, case_id: c.id, phone_number: c.phone_number,
    reason: c.case_type, context: {note: c.summary}, created_at: c.created_at,
    resolved: ['resolved', 'reviewed'].includes(c.status), resolution_note: c.resolution}};
};
export const resolveReviewItem = (issue: any, note?: string) => issue.id
  ? resolveFlag(issue.id, note)
  : agentPost(`cases/${issue.case_id}/action`, {resolution: note || 'Handled by admin', actor: 'admin'});
export const resolveAllFlags = (throughId: number) =>
  agentPost<{ status: string; resolved_count: number }>("review/resolve-all", { through_id: throughId });

// Conversations
export type InboxFilters = Partial<Record<"from_date" | "to_date" | "service" | "area" | "channel" | "party" | "registration" | "testimonial" | "outcome", string>>;
export const getInbox = (tab = "attention", q?: string, limit = 100, filters: InboxFilters = {}) =>
  agentGet<any[]>("api/inbox", { tab, ...(q ? {q} : {}), limit, ...filters });
export const getConversation = (phone: string) =>
  agentGet<any>(`api/conversation/${encodeURIComponent(phone)}`);
/** Takeover state lives on its OWN endpoint (the thread payload has no
 *  `takeover` field). Returns {takeover: <lock|null>, handoff_summary}. */
export const getTakeoverStatus = (phone: string) =>
  agentGet<{ takeover: any | null; handoff_summary: any }>(
    `api/conversation/${encodeURIComponent(phone)}/takeover`);
export const takeoverConversation = (phone: string) =>
  agentPost(`api/conversation/${encodeURIComponent(phone)}/takeover`);
export const releaseConversation = (phone: string, summary?: string) =>
  agentPost(`api/conversation/${encodeURIComponent(phone)}/release`, { summary });
export const sendMessage = (phone: string, textBody: string) =>
  agentPost("send", { phone, text: textBody });

// Agent metrics
export const getMetrics = () => agentGet<any>("metrics");

// Blocklist (derived low-rating + manual overrides)
export const getBlocklist = () =>
  agentGet<{ derived: any[]; overrides: any[] }>("blocklist");
export const addOverride = (provider_id: string, action: "block" | "allow", reason?: string) =>
  agentPost("blocklist", { provider_id, action, reason });
export const removeOverride = (providerId: string) =>
  agentDelete(`blocklist/${encodeURIComponent(providerId)}`);

// Suspensions: providers taken out of matching pending review. Their own RESUME
// cannot lift this, so lifting it here is the ONLY appeal path a human has.
export const getSuspensions = () => agentGet<any[]>("suspensions");
export const suspendProvider = (provider_id: string, reason?: string) =>
  agentPost("suspensions", { provider_id, reason });
export const liftSuspension = (providerId: string) =>
  agentDelete(`suspensions/${encodeURIComponent(providerId)}`);

// Waitlist: unmet demand by trade x area (the recruiter targeting map)
export const getWaitlistDemand = () =>
  agentGet<any[]>("waitlist").then((r) => ({
    ...r,
    // the agent returns {cells: [...]}, the table wants the array
    data: (r.data as any)?.cells ?? r.data,
  }));

// Intros
export const getIntros = (limit = 100) => agentGet<any[]>("intros", { limit });
export const updateIntroStatus = (introId: string, status: string) =>
  agentPost(`intros/${encodeURIComponent(introId)}`, { status });

// Dev allowlist (test routing)
export const getDevProviders = () => agentGet<{ providers: any[] }>("api/dev-providers");
export const addDevProvider = (provider_id: string, name?: string) =>
  agentPost("api/dev-providers", { provider_id, name });
export const removeDevProvider = (providerId: string) =>
  agentDelete(`api/dev-providers/${encodeURIComponent(providerId)}`);
export const getDevCustomers = () => agentGet<{ customers: any[] }>("api/dev-customers");
export const addDevCustomer = (phone: string, note?: string) =>
  agentPost("api/dev-customers", { phone, note });
export const removeDevCustomer = (phone: string) =>
  agentDelete(`api/dev-customers/${encodeURIComponent(phone)}`);

// Force a catalog re-fetch
export const refreshCatalog = () => agentPost<{ status: string; services: number }>("catalog/refresh");
